import { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  UserPlus, Copy, Check, Mail, Trash2, Shield, ShieldCheck, Users2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PERM_LABELS = {
  dashboard: "Dashboard", transactions: "Transactions", banks: "Banking",
  ar: "Receivables (AR)", ap: "Payables (AP)", coa: "Chart of Accounts",
  accounting: "Financial Reports", financials: "Fixed Assets & Tax",
  payroll: "Payroll", inventory: "Inventory", ai: "AI Insights",
  executive: "Executive KPIs", reports: "Statement PDFs", analytics: "Analytics",
  rules: "Rules", budgets: "Budgets", accounts: "Accounts",
  categories: "Categories", goals: "Goals", investments: "Investments",
  recurring: "Recurring", entities: "Entities", team: "Team",
  settings: "Settings",
};

const PERM_GROUPS = [
  { label: "Sales & AR", perms: ["ar"] },
  { label: "Expenses & AP", perms: ["ap"] },
  { label: "Accounting", perms: ["coa", "accounting", "financials", "reports"] },
  { label: "Banking & Transactions", perms: ["transactions", "banks", "rules", "recurring", "categories", "accounts"] },
  { label: "Payroll & Inventory", perms: ["payroll", "inventory"] },
  { label: "Analytics & AI", perms: ["analytics", "executive", "ai"] },
  { label: "Personal Finance", perms: ["budgets", "goals", "investments"] },
  { label: "Admin", perms: ["entities", "team"] },
];

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [presets, setPresets] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: i }, { data: p }] = await Promise.all([
        api.get("/team/members"),
        api.get("/team/invites"),
        api.get("/team/permissions"),
      ]);
      setMembers(m);
      setInvites(i);
      setPresets(p.presets || {});
    } catch (err) {
      console.warn("[Team] load failed:", err?.response?.status, err?.message);
      if (err?.response?.status === 403) {
        toast.error("Only workspace owners can manage the team");
      } else {
        toast.error("Couldn't load team");
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyInvite = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(id);
      setTimeout(() => setCopiedToken(null), 2000);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };

  const cancelInvite = async (id) => {
    try {
      await api.delete(`/team/invites/${id}`);
      toast.success("Invitation cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/team/members/${removeTarget.id}`);
      toast.success(`${removeTarget.email} removed from team`);
      setRemoveTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  if (!user?.is_owner) {
    return (
      <AppShell>
        <div data-testid="team-not-owner" className="card-flat p-8 max-w-lg mx-auto text-center">
          <Shield className="w-8 h-8 mx-auto text-[var(--muted-text)] mb-3" />
          <h2 className="font-display text-xl mb-2">Team management is owner-only</h2>
          <p className="text-sm text-[var(--muted-text)]">
            You're a team member of another workspace. Only the workspace owner can invite or manage team members.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap gap-4 items-end justify-between">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Team</div>
          <h1 className="font-display text-3xl mt-1 text-[var(--text)]">Users & access</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            Invite accountants, bookkeepers or clerks. Grant only the modules they need.
          </p>
        </div>
        <Button
          data-testid="invite-user-btn"
          className="rounded-md btn-brand h-10 px-4 gap-1.5"
          onClick={() => setInviteOpen(true)}
        >
          <UserPlus className="w-4 h-4" /> Invite user
        </Button>
      </div>

      {/* Members table */}
      <div className="card-flat overflow-hidden mb-6" data-testid="members-list">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
          <Users2 className="w-4 h-4 text-[var(--muted-text)]" />
          <span className="font-medium text-sm">Members ({members.length})</span>
        </div>
        <table className="qb-table w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--border)]">
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Access</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} data-testid={`member-row-${m.email}`}>
                <td className="px-4 py-3 font-medium">{m.name || "—"}{m.is_self && <span className="ml-2 text-xs text-[var(--muted-text)]">(you)</span>}</td>
                <td className="px-4 py-3 text-[var(--muted-text)]">{m.email}</td>
                <td className="px-4 py-3">
                  {m.is_owner ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--brand-light)] text-[var(--brand-text)] font-medium">
                      <ShieldCheck className="w-3 h-3" /> Owner
                    </span>
                  ) : (
                    <span className="text-xs text-[var(--muted-text)]">Team member</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[var(--muted-text)]">
                  {m.is_owner ? "Full access" : `${(m.permissions || []).length} modules`}
                </td>
                <td className="px-4 py-3 text-right">
                  {!m.is_owner && (
                    <div className="flex justify-end gap-1">
                      <Button
                        data-testid={`edit-member-${m.email}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingMember(m)}
                      >
                        Edit access
                      </Button>
                      <Button
                        data-testid={`remove-member-${m.email}`}
                        size="icon"
                        variant="ghost"
                        onClick={() => setRemoveTarget(m)}
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4 text-[var(--expense)]" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="card-flat overflow-hidden" data-testid="invites-list">
          <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
            <Mail className="w-4 h-4 text-[var(--muted-text)]" />
            <span className="font-medium text-sm">Pending invitations ({invites.length})</span>
          </div>
          <div className="divide-y divide-[var(--divider)]">
            {invites.map((inv) => (
              <div key={inv.id} data-testid={`invite-row-${inv.email}`} className="px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{inv.email}</div>
                  <div className="text-xs text-[var(--muted-text)]">
                    {(inv.permissions || []).length} modules · expires {formatDistanceToNow(new Date(inv.expires_at), { addSuffix: true })}
                  </div>
                </div>
                <Button
                  data-testid={`copy-invite-${inv.email}`}
                  size="sm"
                  variant="outline"
                  onClick={() => copyInvite(inv.invite_url, inv.id)}
                  className="rounded-md h-8"
                >
                  {copiedToken === inv.id ? (<><Check className="w-3.5 h-3.5 mr-1" /> Copied</>) : (<><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</>)}
                </Button>
                <Button
                  data-testid={`cancel-invite-${inv.email}`}
                  size="sm"
                  variant="ghost"
                  onClick={() => cancelInvite(inv.id)}
                  className="rounded-md h-8"
                >
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => { setInviteOpen(false); load(); }}
        presets={presets}
      />

      {editingMember && (
        <EditMemberDialog
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSaved={() => { setEditingMember(null); load(); }}
        />
      )}

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              They will lose access immediately. Their audit trail on transactions and journal entries remains in your books.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="remove-confirm" onClick={removeMember} className="btn-brand">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

// ---------- Invite dialog ----------
function InviteDialog({ open, onClose, onCreated, presets }) {
  const [email, setEmail] = useState("");
  const [preset, setPreset] = useState("");
  const [perms, setPerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createdInvite, setCreatedInvite] = useState(null);

  useEffect(() => {
    if (open) { setEmail(""); setPreset(""); setPerms([]); setCreatedInvite(null); }
  }, [open]);

  const togglePerm = (p) =>
    setPerms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);

  const applyPreset = (name) => {
    setPreset(name);
    if (name && presets[name]) {
      // Filter out always-granted (dashboard, settings) — they're implicit.
      const explicit = presets[name].filter((p) => p !== "dashboard" && p !== "settings");
      setPerms(explicit);
    }
  };

  const submit = async () => {
    if (!email) { toast.error("Enter an email address"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/team/invites", {
        email,
        permissions: perms,
        preset: preset || null,
      });
      setCreatedInvite(data);
      toast.success(data.email_sent ? "Invitation sent" : "Invitation link ready — send it manually");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!createdInvite) return;
    try {
      await navigator.clipboard.writeText(createdInvite.invite_url);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>
            They will see your books with only the modules you enable below.
          </DialogDescription>
        </DialogHeader>

        {!createdInvite ? (
          <div className="space-y-5">
            <div>
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">Email</Label>
              <Input
                data-testid="invite-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="accountant@company.com"
                className="mt-1 h-10 rounded-md"
              />
            </div>

            <div>
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">Preset (optional)</Label>
              <Select value={preset} onValueChange={applyPreset}>
                <SelectTrigger data-testid="invite-preset" className="mt-1 h-10 rounded-md">
                  <SelectValue placeholder="Custom — pick modules below" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(presets).map((k) => (
                    <SelectItem key={k} value={k} data-testid={`preset-${k}`}>
                      {k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="text-xs tracking-wide uppercase text-[var(--muted-text)] mb-2">
                Module access ({perms.length} selected)
              </div>
              <div className="border border-[var(--border)] rounded-md p-3 max-h-72 overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {PERM_GROUPS.map((g) => (
                  <div key={g.label} className="col-span-full mt-2 first:mt-0">
                    <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[var(--muted-text)] mb-1">
                      {g.label}
                    </div>
                    {g.perms.map((p) => (
                      <label key={p} className="flex items-center gap-2 py-1 cursor-pointer text-sm hover:bg-[var(--hover)] rounded px-1">
                        <Checkbox
                          data-testid={`perm-${p}`}
                          checked={perms.includes(p)}
                          onCheckedChange={() => togglePerm(p)}
                        />
                        <span>{PERM_LABELS[p] || p}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-[var(--muted-text)] mt-2">
                Dashboard and Settings are always granted.
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-[var(--brand-light)] border border-[var(--brand)]/20 rounded-md p-3">
              <div className="flex items-center gap-2 text-[var(--brand-text)] text-sm font-medium">
                <Check className="w-4 h-4" /> Invitation created for {createdInvite.email}
              </div>
              {createdInvite.email_sent
                ? <div className="text-xs text-[var(--brand-text)] mt-1">Email delivered via Resend.</div>
                : <div className="text-xs text-[var(--brand-text)] mt-1">
                    Email couldn't be sent{createdInvite.email_error ? ` (${createdInvite.email_error})` : ""}. Copy the link below and share it directly.
                  </div>}
            </div>
            <div>
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">Invite link</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  data-testid="invite-link-input"
                  readOnly
                  value={createdInvite.invite_url}
                  className="font-mono-data text-xs h-10 rounded-md"
                />
                <Button data-testid="invite-copy-btn" onClick={copy} className="rounded-md h-10 btn-brand px-3 shrink-0">
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
              </div>
              <div className="text-[11px] text-[var(--muted-text)] mt-1">Expires in 7 days.</div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!createdInvite ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="invite-cancel">Cancel</Button>
              <Button onClick={submit} disabled={saving || !email} className="btn-brand" data-testid="invite-submit">
                {saving ? "Sending…" : "Send invitation"}
              </Button>
            </>
          ) : (
            <Button onClick={() => { onCreated(); }} className="btn-brand" data-testid="invite-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Edit member dialog ----------
function EditMemberDialog({ member, onClose, onSaved }) {
  const [perms, setPerms] = useState(member.permissions || []);
  const [saving, setSaving] = useState(false);

  const togglePerm = (p) =>
    setPerms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/team/members/${member.id}`, { permissions: perms });
      toast.success("Access updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle>Edit access for {member.email}</DialogTitle>
          <DialogDescription>
            Changes take effect on their next request (their current session is invalidated).
          </DialogDescription>
        </DialogHeader>

        <div className="border border-[var(--border)] rounded-md p-3 max-h-72 overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {PERM_GROUPS.map((g) => (
            <div key={g.label} className="col-span-full mt-2 first:mt-0">
              <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[var(--muted-text)] mb-1">
                {g.label}
              </div>
              {g.perms.map((p) => (
                <label key={p} className="flex items-center gap-2 py-1 cursor-pointer text-sm hover:bg-[var(--hover)] rounded px-1">
                  <Checkbox
                    data-testid={`edit-perm-${p}`}
                    checked={perms.includes(p)}
                    onCheckedChange={() => togglePerm(p)}
                  />
                  <span>{PERM_LABELS[p] || p}</span>
                </label>
              ))}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="btn-brand" data-testid="edit-save">
            {saving ? "Saving…" : "Save access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
